<template>
  <BaseModal
    :modalTitle="$t('W: Skift kunde')"
    :modalIntro="$t('W: Vælg den kunde og bruger, ordren i stedet skal høre til')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <p class="mb-4 text-sm text-text-secondary">
        <span class="font-semibold text-text-primary">{{ $t('W: Nuværende kunde') }}:</span>
        {{ currentOwnerText }}
      </p>

      <div class="mb-4 flex items-center gap-2">
        <button
          v-for="option in customerTypeOptions"
          :key="option.value"
          type="button"
          :data-testid="`customer-type-${option.value}`"
          :class="[
            'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
            form.customerType === option.value
              ? 'bg-action text-white'
              : 'bg-surface-muted text-text-secondary hover:bg-surface-sunken',
          ]"
          @click="form.customerType = option.value">
          {{ option.text }}
        </button>
      </div>

      <UiComboboxVee
        v-if="form.customerType === 'business'"
        name="customerAccountID"
        :label="$t('W: Kunde')"
        :rules="['required']"
        isRequired
        showAsRequired
        v-model="form.customerAccountID"
        :options="customerAccountOptions" />

      <UiComboboxVee
        v-if="form.customerType === 'insurance'"
        name="insuranceCustomerAccountID"
        :label="$t('W: Forsikringskunde')"
        :rules="['required']"
        isRequired
        showAsRequired
        v-model="form.insuranceCustomerAccountID"
        :options="insuranceCustomerAccountOptions" />

      <UiComboboxVee
        v-if="form.customerType !== 'private'"
        name="customerUserID"
        :label="$t('W: Bruger')"
        :rules="['required']"
        isRequired
        showAsRequired
        :isDisabled="!selectedCompanyCustomer"
        v-model="form.customerUserID"
        :options="customerUserOptions" />

      <UiComboboxVee
        v-else
        name="privateUserID"
        :label="$t('W: Privatkunde')"
        :rules="['required']"
        isRequired
        showAsRequired
        v-model="form.privateUserID"
        :options="customerUsersPrivateOptions" />

      <!-- An insurance customer's own order always carries its skadenr. — asked for here only when
           the order has none yet. -->
      <UiInputTextFieldVee
        v-if="requiresInsuranceNumber"
        name="insuranceNumber"
        :label="$t('W: Skadenr.')"
        :rules="['required']"
        isRequired
        showAsRequired
        v-model="form.insuranceNumber" />

      <div
        class="mt-2 mb-4 flex flex-col gap-3"
        aria-live="polite">
        <InfoText
          v-if="previewError"
          theme="warning"
          :text="previewError" />

        <template v-else-if="preview">
          <ul class="list-disc pl-5 text-sm text-text-primary">
            <li
              v-for="line in previewLines"
              :key="line">
              {{ line }}
            </li>
          </ul>
          <InfoText
            v-for="warning in preview.warnings"
            :key="warning"
            theme="warning"
            :text="warningText(warning)" />
        </template>

        <span
          v-else-if="isPreviewLoading"
          class="text-sm text-text-muted">
          {{ $t('W: Beregner ændringer…') }}
        </span>
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.cancel')"
          theme="neutral"
          @click="$emit('close')" />
        <UiBaseButton
          :text="$t('W: Skift kunde')"
          type="submit"
          :isDisabled="!canSubmit"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { useAppStore } from '@/stores/appStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useToast } from '@/globals/typescript/utils/toast'
import { getRefusalReason } from '@/globals/typescript/utils/apiRefusal'
import { customerKey } from '@/globals/typescript/utils/customerOwner'
import { resolveOrderOwner } from '@/globals/typescript/utils/orderOwner'
import type {
  TChangeOrderCustomerBody,
  TChangeOrderCustomerResult,
  TChangeOrderCustomerWarning,
} from '@/globals/typescript/utils/orderCustomerChange'

type TCustomerType = 'business' | 'insurance' | 'private'

/** How long the selection must rest before the preview asks the server what would change. */
const PREVIEW_DELAY_MS = 250

/**
 * Changes who an order belongs to (MIL-608): pick the kind of customer, the account and an existing
 * user, and see — before anything is saved — what the change does: prices, insurance, report
 * recipients, project. The preview is the server's own dry run, so what is shown is what the save
 * does, and a refusal (an invoice locks the order) shows up here with its reason instead of after
 * the click. No users are created from here.
 */
export default {
  name: 'ChangeOrderCustomerModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        customerType: 'business' as TCustomerType,
        customerAccountID: '',
        insuranceCustomerAccountID: '',
        customerUserID: '',
        privateUserID: '',
        insuranceNumber: '',
      },
      preview: null as TChangeOrderCustomerResult | null,
      previewError: '',
      isPreviewLoading: false,
      previewSeq: 0,
      previewTimer: null as ReturnType<typeof setTimeout> | null,
      errorText: '',
      isLoading: false,
      showSaveIcon: false,
    }
  },

  computed: {
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useCustomerAccountStore, ['allCustomerAccounts', 'customerAccountDisplayNamesByID']),
    ...mapState(useCustomerUserStore, ['customerUsers', 'customerUsersByCustomerKey', 'customerUsersPrivate']),
    ...mapState(useInsuranceCustomerAccountStore, ['allInsuranceCustomerAccounts']),

    order() {
      return this.allOrders.find((x) => x.id === this.orderID) ?? null
    },

    currentOwnerText(): string {
      if (!this.order) return '—'
      const owner = resolveOrderOwner(this.order, {
        customerAccountsById: Object.fromEntries(this.allCustomerAccounts.map((x) => [x.id, x])),
        insuranceCustomerAccountsById: Object.fromEntries(
          this.allInsuranceCustomerAccounts.map((x) => [x.id, x])
        ),
      })
      const userID = this.order.customerUserID || this.order.privateUserID
      const user = this.customerUsers.find((x) => x.id === userID)
      return [owner.name || '—', user?.fullName].filter(Boolean).join(' · ')
    },

    customerTypeOptions(): { value: TCustomerType; text: string }[] {
      return [
        { value: 'business', text: this.$t('W: Erhverv') },
        { value: 'insurance', text: this.$t('W: Forsikring') },
        { value: 'private', text: this.$t('W: Privat') },
      ]
    },

    customerAccountOptions(): { value: string; text: string }[] {
      return this.allCustomerAccounts
        .filter((x) => x.activeState === 'active')
        .map((customerAccount) => ({
          value: customerAccount.id,
          text: this.customerAccountDisplayNamesByID?.[customerAccount.id] ?? customerAccount.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    insuranceCustomerAccountOptions(): { value: string; text: string }[] {
      return this.allInsuranceCustomerAccounts
        .filter((x) => x.activeState === 'active')
        .map((account) => ({ value: account.id, text: account.name }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    /** The business or insurance account picked, whose users are offered — null until one is. */
    selectedCompanyCustomer(): { type: 'business' | 'insurance'; id: string } | null {
      if (this.form.customerType === 'business' && this.form.customerAccountID) {
        return { type: 'business', id: this.form.customerAccountID }
      }
      if (this.form.customerType === 'insurance' && this.form.insuranceCustomerAccountID) {
        return { type: 'insurance', id: this.form.insuranceCustomerAccountID }
      }
      return null
    },

    // Existing users only — creating one is not part of changing an order's customer.
    customerUserOptions(): { value: string; text: string }[] {
      const users = this.selectedCompanyCustomer
        ? (this.customerUsersByCustomerKey[customerKey(this.selectedCompanyCustomer)] ?? [])
        : []
      return users
        .filter((x) => x.activeState === 'active')
        .map((user) => ({ value: user.id, text: user.fullName }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    customerUsersPrivateOptions(): { value: string; text: string }[] {
      return this.customerUsersPrivate
        .filter((x) => x.activeState === 'active')
        .map((user) => ({ value: user.id, text: user.fullName }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    /** Moving to an insurance customer needs the skadenr. — asked for only if the order has none. */
    requiresInsuranceNumber(): boolean {
      return this.form.customerType === 'insurance' && !this.order?.insuranceNumber?.trim()
    },

    /** The user the order has now — the ordering user, or the private customer. */
    currentUserID(): string {
      return this.order?.customerUserID || this.order?.privateUserID || ''
    },

    /**
     * The selection is exactly what the order already has — the dialog opens like this, and the lab
     * may pick their way back to it. Nothing would change, so there is nothing to preview or save.
     * A skadenr. typed on an insurance customer's own order that has none still counts as a change.
     */
    isCurrentSelection(): boolean {
      const current = this.order?.customer
      const { customerType, customerAccountID, insuranceCustomerAccountID, customerUserID, privateUserID } = this.form
      if (!current || current.type !== customerType) return false
      if (customerType === 'private') return privateUserID === this.currentUserID
      if (customerUserID !== this.currentUserID) return false
      if (customerType === 'business') return customerAccountID === current.id
      return insuranceCustomerAccountID === current.id && !this.form.insuranceNumber.trim()
    },

    /** The request, once the selection is complete and differs from what the order has; null before that. */
    requestBody(): TChangeOrderCustomerBody | null {
      if (this.isCurrentSelection) return null
      const { customerType, customerAccountID, insuranceCustomerAccountID, customerUserID, privateUserID } = this.form
      if (customerType === 'private') {
        return privateUserID ? { customerType, privateUserID } : null
      }
      if (customerType === 'business') {
        return customerAccountID && customerUserID ? { customerType, customerAccountID, customerUserID } : null
      }
      if (!insuranceCustomerAccountID || !customerUserID) return null
      const insuranceNumber = this.form.insuranceNumber.trim()
      return {
        customerType,
        insuranceCustomerAccountID,
        customerUserID,
        ...(insuranceNumber ? { insuranceNumber } : {}),
      }
    },

    canSubmit(): boolean {
      return (
        !!this.requestBody &&
        !!this.preview &&
        !this.previewError &&
        !this.isPreviewLoading &&
        (!this.requiresInsuranceNumber || !!this.form.insuranceNumber.trim())
      )
    },

    /** The plain effects of the change, in words — what the lab confirms by saving. */
    previewLines(): string[] {
      const preview = this.preview
      if (!preview) return []
      const lines: string[] = []
      if (preview.pricesReset) {
        lines.push(this.$t('W: Priserne nulstilles og beregnes på ny for den nye kunde'))
      } else if (!preview.ownerChanged) {
        lines.push(this.$t('W: Kun brugeren skiftes — priser og forsikring er uændrede'))
      }
      if (preview.insurance.outcome === 'forced') {
        lines.push(this.$t('W: Ordren bliver forsikringskundens egen forsikringssag'))
      } else if (preview.insurance.outcome === 'kept') {
        lines.push(this.$t('W: Forsikringsselskabet {name} beholdes', { name: this.insurerName(preview.insurance.insurancePartnerID) }))
      } else if (preview.insurance.outcome !== 'unchanged') {
        lines.push(this.$t('W: Forsikringen fjernes fra ordren'))
      }
      if (preview.recipients.before.join(',') !== preview.recipients.after.join(',')) {
        lines.push(
          this.$t('W: Modtagere af rapporter: {emails}', { emails: preview.recipients.after.join(', ') || '—' })
        )
      }
      return lines
    },
  },

  watch: {
    'form.customerType'() {
      this.form.customerAccountID = ''
      this.form.insuranceCustomerAccountID = ''
      this.form.customerUserID = ''
      this.form.privateUserID = ''
      this.form.insuranceNumber = ''
      this.preselectCurrentCustomer()
    },
    // A user belongs to one account — a new account clears the user picked for the old one.
    'form.customerAccountID'() {
      this.clearUserOutsideSelectedAccount()
    },
    'form.insuranceCustomerAccountID'() {
      this.clearUserOutsideSelectedAccount()
    },
    requestBody: {
      handler() {
        this.schedulePreview()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['changeOrderCustomer']),

    /**
     * Starts on the order's own customer and user when the kind of customer is the order's own
     * kind, so changing only the user is a single pick and cannot move the order to another
     * account by accident. An account or user that is no longer offered (inactive) is not
     * preselected.
     */
    preselectCurrentCustomer(): void {
      const current = this.order?.customer
      if (!current?.id || current.type !== this.form.customerType) return
      if (current.type === 'private') {
        if (this.customerUsersPrivateOptions.some((x) => x.value === this.currentUserID)) {
          this.form.privateUserID = this.currentUserID
        }
        return
      }
      if (current.type === 'business' && this.customerAccountOptions.some((x) => x.value === current.id)) {
        this.form.customerAccountID = current.id
      } else if (
        current.type === 'insurance' &&
        this.insuranceCustomerAccountOptions.some((x) => x.value === current.id)
      ) {
        this.form.insuranceCustomerAccountID = current.id
      } else {
        return
      }
      // Only offered users can be selected, so an inactive current user is left for the lab to replace.
      if (this.customerUserOptions.some((x) => x.value === this.currentUserID)) {
        this.form.customerUserID = this.currentUserID
      }
    },

    clearUserOutsideSelectedAccount(): void {
      if (!this.customerUserOptions.some((x) => x.value === this.form.customerUserID)) {
        this.form.customerUserID = ''
      }
    },

    insurerName(insurerID: string | null): string {
      return this.allInsuranceCustomerAccounts.find((x) => x.id === insurerID)?.name ?? '—'
    },

    warningText(warning: TChangeOrderCustomerWarning): string {
      switch (warning) {
        case 'insurance-cleared-private':
          return this.$t('W: Forsikringen fjernes — en privat ordre kan ikke have forsikring')
        case 'insurance-cleared-not-mapped':
          return this.$t('W: Forsikringen fjernes — den nye kunde er ikke knyttet til forsikringsselskabet')
        case 'insurer-kept-payer-unset':
          return this.$t('W: "Endelig betaler" nulstilles — vælg den igen på fakturafanen')
        case 'insurer-replaced':
          return this.$t('W: Ordrens forsikringsselskab erstattes af den nye forsikringskunde')
        case 'project-cleared':
          return this.$t('W: Ordren fjernes fra sit projekt')
        case 'reports-already-generated':
          return this.$t('W: Allerede oprettede rapporter viser stadig den gamle kunde — opret og send dem igen')
        case 'is-internal-differs':
          return this.$t('W: Ordrens interne/eksterne status følger ikke den nye kunde — brug Intern/Ekstern på fakturafanen')
        case 'report-locale-differs':
          return this.$t('W: Rapportens sprog følger ikke den nye kunde — ret det under rapportsprog')
        case 'addon-prices-kept':
          return this.$t('W: Tillægsydelser beholder deres gamle priser — tjek dem på fakturafanen')
      }
    },

    /** The selection changed: forget the preview and ask again once it has rested. */
    schedulePreview(): void {
      if (this.previewTimer) clearTimeout(this.previewTimer)
      this.previewSeq++
      this.preview = null
      this.previewError = ''
      if (!this.requestBody) {
        this.isPreviewLoading = false
        return
      }
      this.isPreviewLoading = true
      this.previewTimer = setTimeout(() => void this.runPreview(), PREVIEW_DELAY_MS)
    },

    async runPreview(): Promise<void> {
      const body = this.requestBody
      if (!body) return
      const seq = ++this.previewSeq
      try {
        const result = await this.changeOrderCustomer(this.orderID, { ...body, dryRun: true })
        if (seq !== this.previewSeq) return
        this.preview = result
      } catch (error) {
        if (seq !== this.previewSeq) return
        this.previewError = getRefusalReason(error) ?? this.$t('error.common.standard')
      } finally {
        if (seq === this.previewSeq) this.isPreviewLoading = false
      }
    },

    async onSubmit(): Promise<void> {
      const body = this.requestBody
      if (!body || !this.canSubmit) return

      this.isLoading = true
      this.errorText = ''
      try {
        await this.changeOrderCustomer(this.orderID, body)
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Kunden er skiftet'))
        this.$emit('close')
      } catch (error) {
        this.errorText = getRefusalReason(error) ?? this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke skifte kunde'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.form.customerType = this.order?.customer?.type ?? (this.order?.segment === 'private' ? 'private' : 'business')
    this.preselectCurrentCustomer()
    // The modal is always the top entry at the moment it is created, so this is its own ModalEntry id.
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    if (this.previewTimer) clearTimeout(this.previewTimer)
    this.unsavedGuard.unregister()
  },
}
</script>
